import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Hostess = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', play_mode: 'whatsapp', bottles_count: 2 });
  const [ticketPhoto, setTicketPhoto] = useState(null);
  const [ticketPreview, setTicketPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fileInputRef = useRef(null);

  const loadSessionInfo = useCallback(async () => {
    try {
      const response = await api.get('/hostess/active-session');
      setSessionInfo(response.data);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors du chargement de la session' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessionInfo();
  }, [loadSessionInfo]);

  const handleSubmit = async (mode) => {
    // Validation: nom obligatoire
    if (!formData.name) {
      setMessage({ type: 'danger', text: 'Veuillez remplir le nom' });
      return;
    }

    // Validation: téléphone obligatoire pour tous les modes
    if (!formData.phone) {
      setMessage({ type: 'danger', text: 'Veuillez remplir le numéro de téléphone' });
      return;
    }

    // Validation: photo du ticket obligatoire
    if (!ticketPhoto) {
      setMessage({ type: 'danger', text: 'Veuillez prendre une photo du ticket de caisse' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    const formDataObj = new FormData();
    formDataObj.append('name', formData.name);
    formDataObj.append('phone', formData.phone);
    formDataObj.append('play_mode', mode);
    formDataObj.append('bottles_count', formData.bottles_count);
    if (ticketPhoto) {
      formDataObj.append('ticket_photo', ticketPhoto);
    }

    try {
      const response = await api.post('/hostess/register-participant', formDataObj);

      if (mode === 'whatsapp') {
        // Mode WhatsApp: ouvrir directement WhatsApp avec le message pré-rempli
        const whatsappLink = response.data.whatsapp_link;

        setMessage({ type: 'success', text: `Participant enregistré ! Ouverture de WhatsApp...` });

        // Ouvrir WhatsApp dans un nouvel onglet
        if (whatsappLink) {
          window.open(whatsappLink, '_blank');
        }
      } else {
        // Mode Direct: rediriger vers la page de jeu
        const token = response.data.game_token;
        if (!token) {
          setMessage({ type: 'danger', text: 'Erreur: token de jeu non généré' });
          return;
        }
        setMessage({ type: 'success', text: 'Participant enregistré ! Redirection vers le jeu...' });
        setTimeout(() => {
          navigate(`/play/${token}`);
        }, 500);
        return;
      }

      setFormData({ name: '', phone: '', play_mode: 'whatsapp', bottles_count: 2 });
      setTicketPhoto(null);
      setTicketPreview(null);
      loadSessionInfo();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de l\'enregistrement' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Gestion de la photo - Input file natif pour mobile
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTicketPhoto(file);
      setTicketPreview(URL.createObjectURL(file));
    }
  };

  const clearPhoto = () => {
    if (window.confirm('Supprimer cette photo ?')) {
      setTicketPhoto(null);
      setTicketPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #FFD700 0%, #1a1a2e 100%)' }}>
        <div className="text-center text-white">
          <div className="mb-4">
            <div style={{ width: '60px', height: '60px', margin: '0 auto', position: 'relative' }}>
              <div className="spinner-border" role="status" style={{ width: '100%', height: '100%', borderWidth: '4px' }}></div>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <i className="fas fa-bolt" style={{ fontSize: '24px' }}></i>
              </div>
            </div>
          </div>
          <p className="mb-0 fw-bold" style={{ fontSize: 'clamp(14px, 3vw, 18px)' }}>CHARGEMENT...</p>
        </div>
      </div>
    );
  }

  if (!sessionInfo || !sessionInfo.active) {
    return (
      <div className="min-vh-100 d-flex flex-column" style={{ background: 'linear-gradient(135deg, #FFD700 0%, #1a1a2e 100%)' }}>
        <nav className="navbar navbar-expand-lg navbar-dark" style={{ background: 'rgba(26, 26, 46, 0.5)', backdropFilter: 'blur(10px)' }}>
          <div className="container">
            <a className="navbar-brand fw-bold d-flex align-items-center" href="/">
              <div style={{
                width: '38px', height: '38px',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginRight: '10px'
              }}>
                <i className="fas fa-bolt text-dark" style={{ fontSize: '16px' }}></i>
              </div>
              <span style={{ fontSize: 'clamp(18px, 4vw, 24px)', letterSpacing: '1px' }}>REAKTOR</span>
            </a>
            <div className="d-flex align-items-center">
              <span className="text-white me-2 d-none d-sm-inline">
                <i className="fas fa-user-tag me-1"></i>{user?.username}
              </span>
              <button onClick={handleLogout} className="btn btn-outline-light btn-sm" style={{ fontSize: '13px' }}>
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
        </nav>

        <div className="container flex-grow-1 d-flex align-items-center py-3">
          <div className="card border-0 shadow-lg w-100" style={{ borderRadius: '20px', background: 'rgba(26, 26, 46, 0.95)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
            <div className="card-body py-4 py-md-5 text-center">
              <div style={{ width: '70px', height: '70px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <i className="fas fa-calendar-times" style={{ fontSize: '28px', color: '#000' }}></i>
              </div>
              <h3 className="mb-2 fw-bold text-white" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>Aucune session active</h3>
              <p className="text-light mb-3" style={{ fontSize: 'clamp(13px, 2.5vw, 15px)' }}>
                Il n'y a pas de session de jeu active pour votre point de vente.<br />
                Veuillez contacter un administrateur.
              </p>
              <button onClick={loadSessionInfo} className="btn btn-lg px-4" style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#000', border: 'none', fontWeight: 'bold', fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
                <i className="fas fa-sync-alt me-2"></i>Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #FFD700 0%, #1a1a2e 100%)' }}>
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: 'rgba(26, 26, 46, 0.8)', backdropFilter: 'blur(10px)', padding: '8px 0' }}>
        <div className="container">
          <a className="navbar-brand fw-bold d-flex align-items-center" href="/" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>
            <div style={{
              width: 'clamp(32px, 6vw, 42px)', height: 'clamp(32px, 6vw, 42px)',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginRight: '10px'
            }}>
              <i className="fas fa-bolt text-dark" style={{ fontSize: 'clamp(14px, 3vw, 18px)' }}></i>
            </div>
            <span style={{ letterSpacing: '1px' }}>REAKTOR</span>
          </a>
          <div className="d-flex align-items-center">
            <span className="text-white me-2 d-none d-md-block">
              <small style={{ fontSize: '10px', display: 'block' }}>Connecté en tant que</small>
              <strong style={{ fontSize: '13px' }}><i className="fas fa-user-tag me-1"></i>{user?.username}</strong>
            </span>
            <button onClick={handleLogout} className="btn btn-outline-light btn-sm" style={{ padding: '6px 10px', fontSize: '13px' }}>
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      </nav>

      <div className="container py-3 py-md-4">
        {message.text && (
          <div className={'alert alert-' + (message.type === 'success' ? 'success' : 'danger') + ' alert-dismissible fade show mb-3 shadow-sm'} role="alert" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Session Info Bar - Responsive */}
        <div className="card border-0 shadow mb-3" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
          <div className="card-body py-2 py-md-3">
            <div className="row align-items-center g-2 g-md-3">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-calendar-check text-dark" style={{ fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Session</small>
                    <strong className="d-block text-white" style={{ fontSize: '13px' }}>{sessionInfo.session_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: 'rgba(255, 215, 0, 0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-store" style={{ color: '#FFD700', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PDV</small>
                    <strong className="d-block text-white" style={{ fontSize: '13px' }}>{sessionInfo.pdv_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: 'rgba(255, 215, 0, 0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-clock" style={{ color: '#FFD700', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Horaires</small>
                    <strong className="d-block text-white" style={{ fontSize: '11px' }}>
                      {new Date(sessionInfo.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(sessionInfo.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 g-md-4">
          {/* Registration Form */}
          <div className="col-12">
            <div className="card border-0 shadow" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center mb-3">
                  <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-user-plus text-dark" style={{ fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-white" style={{ fontSize: 'clamp(14px, 2.5vw, 16px)' }}>Nouveau Participant</h6>
                    <small className="text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>Enregistrez un client pour le jeu</small>
                  </div>
                </div>

                <form onSubmit={(e) => e.preventDefault()}>
                  {/* Photo Section - EN PREMIER */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                      <i className="fas fa-camera me-2" style={{ color: '#FFD700' }}></i>1. Photo du ticket
                    </label>
                    {ticketPreview ? (
                      <div className="position-relative">
                        <div className="card border-2" style={{ borderColor: '#FFD700', background: 'rgba(255, 215, 0, 0.1)' }}>
                          <div className="card-body p-2 text-center">
                            <img
                              src={ticketPreview}
                              alt="Ticket"
                              style={{ maxHeight: '150px', width: '100%', objectFit: 'contain', borderRadius: '8px' }}
                            />
                            <div className="mt-2">
                              <span className="badge" style={{ background: '#FFD700', color: '#000', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                                <i className="fas fa-check-circle me-1"></i>Photo OK
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearPhoto}
                          className="position-absolute top-0 end-0 btn btn-danger btn-sm shadow m-2"
                          title="Supprimer la photo"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="btn w-100 py-2 py-md-3 border-2"
                          style={{ background: 'transparent', borderColor: '#FFD700', color: '#FFD700', fontSize: 'clamp(13px, 2.5vw, 15px)' }}
                        >
                          <i className="fas fa-camera me-2"></i>
                          <span className="fw-semibold">Prendre une photo</span>
                        </button>
                        {/* Input file caché qui déclenche la caméra sur mobile */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                      </>
                    )}
                  </div>

                  {/* Informations du participant */}
                  <div className="row g-2 g-md-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>2. Nom</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: 'rgba(255, 215, 0, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#FFD700', padding: '10px 12px' }}><i className="fas fa-user"></i></span>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Ex: Jean Dupont"
                          required
                          style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff', fontSize: 'clamp(13px, 2.5vw, 16px)' }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>3. Téléphone</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: 'rgba(255, 215, 0, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#FFD700', padding: '10px 12px' }}><i className="fas fa-phone"></i></span>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="6XXXXXXXX (obligatoire)"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff', fontSize: 'clamp(13px, 2.5vw, 16px)' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row g-2 g-md-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>4. Bouteilles</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: 'rgba(255, 215, 0, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#FFD700', padding: '10px 12px' }}><i className="fas fa-wine-bottle"></i></span>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => setFormData({ ...formData, bottles_count: Math.max(2, formData.bottles_count - 1) })}
                          style={{ background: 'rgba(255, 215, 0, 0.2)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#FFD700', padding: '10px 15px' }}
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <input
                          type="number"
                          className="form-control text-center"
                          min="2"
                          value={formData.bottles_count}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setFormData({ ...formData, bottles_count: val >= 2 ? val : 2 });
                          }}
                          required
                          style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff', fontSize: 'clamp(16px, 3vw, 20px)', fontWeight: 'bold' }}
                        />
                        <button
                          type="button"
                          className="btn"
                          onClick={() => setFormData({ ...formData, bottles_count: formData.bottles_count + 1 })}
                          style={{ background: 'rgba(255, 215, 0, 0.2)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#FFD700', padding: '10px 15px' }}
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>5. Mode de jeu</label>
                      <div className="row g-2">
                        <div className="col-6">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: '#25D366',
                              color: '#fff',
                              border: '2px solid #25D366',
                              fontSize: 'clamp(12px, 2.5vw, 14px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('whatsapp')}
                          >
                            <i className="fab fa-whatsapp me-1"></i>
                            <div className="fw-semibold">WhatsApp</div>
                            <small style={{ fontSize: 'clamp(9px, 2vw, 11px)' }}>Envoyer au client</small>
                          </button>
                        </div>
                        <div className="col-6">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                              color: '#000',
                              border: '2px solid #FFD700',
                              fontSize: 'clamp(12px, 2.5vw, 14px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('direct')}
                          >
                            <i className="fas fa-mobile-alt me-1"></i>
                            <div className="fw-semibold">Jouer Direct</div>
                            <small style={{ fontSize: 'clamp(9px, 2vw, 11px)' }}>Sur ce mobile</small>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hostess;
